import { Link } from "react-router-dom";

export default function FirstTimeBuyerLoanTypesPost() {
    return (
        <>
            <p>
                If you&apos;re buying your first home in 2026, you&apos;re going
                to hear three loan acronyms thrown at you: <strong>FHA</strong>,
                <strong> VA</strong>, and <strong>conventional</strong>. Each
                comes from a different corner of the housing-finance system,
                each has wildly different costs over the life of the loan, and
                most lenders will quote you whichever one earns them the most —
                not whichever one is best for you. Here&apos;s the honest
                breakdown.
            </p>

            <h2>Conventional loans — the default option</h2>
            <p>
                Conventional means a loan that conforms to the rules set by
                Fannie Mae and Freddie Mac. As of 2025, the conforming limit is
                $766,550 for a single-family home (higher in high-cost areas
                like LA and NYC, where it tops out around $1.15M).
            </p>
            <ul>
                <li><strong>Down payment:</strong> 3% minimum (some programs), 5% typical, 20% to skip PMI.</li>
                <li><strong>Credit score:</strong> 620 minimum at most lenders, 740+ for best rates.</li>
                <li><strong>PMI:</strong> Required if you put less than 20% down. Costs roughly 0.4%–1.2% of the loan annually. <em>Removable</em> once you reach 20% equity (cancel it — most homeowners forget).</li>
                <li><strong>Best for:</strong> Buyers with strong credit (700+), at least 5–10% down, and a clean financial picture.</li>
            </ul>
            <p>
                Run actual numbers in our{" "}
                <Link to="/calculator/mortgage">mortgage calculator</Link> to
                see what monthly payment a 20%-down conventional loan looks
                like at today&apos;s rates.
            </p>

            <h2>FHA loans — the lower-bar entry point</h2>
            <p>
                Backed by the Federal Housing Administration. The FHA insures
                the loan; private lenders fund it. The whole point is to make
                first-time homeownership accessible to buyers who can&apos;t
                quite hit conventional standards.
            </p>
            <ul>
                <li><strong>Down payment:</strong> 3.5% minimum (with credit score 580+).</li>
                <li><strong>Credit score:</strong> 500 minimum (with 10% down), 580 for the 3.5% down option.</li>
                <li><strong>Mortgage Insurance Premium (MIP):</strong>
                    <ul>
                        <li><strong>Upfront:</strong> 1.75% of loan amount, financeable into the loan.</li>
                        <li><strong>Annual:</strong> 0.55% of loan balance (most cases), paid monthly.</li>
                        <li><strong>NOT cancellable</strong> for the life of the loan if you put less than 10% down. This is the FHA&apos;s biggest hidden cost — most buyers don&apos;t realize they&apos;ll pay MIP for 30 years unless they refinance to conventional later.</li>
                    </ul>
                </li>
                <li><strong>Loan limits:</strong> $498,257 for most counties (2025); higher in high-cost areas.</li>
                <li><strong>Best for:</strong> First-time buyers with credit 580–680 or down payment under 5%.</li>
            </ul>
            <p>
                Plug your numbers into our{" "}
                <Link to="/calculator/fha-loan">FHA loan calculator</Link> —
                it includes the upfront 1.75% UFMIP and the 0.55% annual MIP
                in the monthly figure.
            </p>

            <h2>VA loans — the unbeatable option (if you&apos;re eligible)</h2>
            <p>
                Backed by the Department of Veterans Affairs for active-duty
                service members, veterans, and surviving spouses. If you&apos;re
                eligible and don&apos;t use a VA loan, you&apos;re leaving real
                money on the table.
            </p>
            <ul>
                <li><strong>Down payment:</strong> 0% required. Yes, zero.</li>
                <li><strong>PMI / MIP:</strong> None. Ever. This alone often saves $1,500–$3,000/year.</li>
                <li><strong>Funding fee:</strong> One-time fee, typically 2.15% of loan amount for first-time use (lower for subsequent uses, waived if you have service-connected disability). Financeable into the loan.</li>
                <li><strong>Credit score:</strong> No VA-set minimum, but most lenders want 580–620+.</li>
                <li><strong>Limits:</strong> No loan limit for buyers with full entitlement (since 2020). You can buy a $2M home with $0 down if you qualify and the bank approves.</li>
                <li><strong>Best for:</strong> Anyone eligible. Period.</li>
            </ul>
            <p>
                Run a VA scenario in our{" "}
                <Link to="/calculator/va-loan">VA loan calculator</Link> with
                the funding fee included.
            </p>

            <h2>Worked comparison: $400K home, 30-year fixed at 7%</h2>
            <p>
                Let&apos;s see what each loan type looks like for a buyer with
                $20K cash, decent credit (680), and a $400K target home:
            </p>
            <ul>
                <li><strong>Conventional 5% down:</strong> $20K down, $380K loan, ~$200/mo PMI until 20% equity. Monthly ≈ <strong>$2,728</strong> (P&amp;I + PMI, before tax/insurance).</li>
                <li><strong>FHA 3.5% down:</strong> $14K down, $386K loan + 1.75% UFMIP financed = $392.7K. Monthly ≈ <strong>$2,791</strong> (P&amp;I + 0.55% annual MIP for life of loan).</li>
                <li><strong>VA 0% down:</strong> $0 down, $400K loan + 2.15% funding fee financed = $408.6K. No PMI/MIP. Monthly ≈ <strong>$2,719</strong> (just P&amp;I).</li>
            </ul>
            <p>
                Counterintuitively, the VA loan with zero down has a similar
                monthly to conventional with 5% down — and lower than FHA
                because there&apos;s no recurring MIP. Over 30 years, the VA
                buyer saves $30K–$60K vs FHA on insurance alone.
            </p>

            <h2>Decision tree (the 30-second version)</h2>
            <ul>
                <li><strong>Eligible for VA?</strong> → Use VA. Almost always the winner.</li>
                <li><strong>Credit 740+, 20%+ down?</strong> → Conventional, no PMI. Cleanest path.</li>
                <li><strong>Credit 680+, 5–19% down?</strong> → Conventional with PMI. Plan to drop PMI at 20% equity.</li>
                <li><strong>Credit 580–680, less than 5% down?</strong> → FHA. Refinance to conventional once your credit + equity improve.</li>
                <li><strong>High-cost area, loan over $766,550?</strong> → <Link to="/calculator/jumbo-loan">Jumbo loan</Link> territory. Stricter underwriting, often 10–20% down required.</li>
                <li><strong>Already own and need cash from equity?</strong> → <Link to="/calculator/heloc">HELOC</Link> or cash-out refi, not a new purchase loan.</li>
            </ul>

            <h2>One trap to avoid</h2>
            <p>
                FHA and conventional with PMI are sometimes pitched as
                &ldquo;equivalent.&rdquo; They&apos;re not. PMI on conventional
                drops off automatically once you reach 20% equity. FHA MIP
                does <em>not</em> drop off if you put less than 10% down —
                it&apos;s for the life of the loan unless you refinance. If
                your plan is &ldquo;I&apos;ll just refinance to conventional in
                3 years,&rdquo; check refinance rates today before you commit.
                In a high-rate environment (where we sit in 2026), refinancing
                a 7% FHA into a 7.5% conventional <em>costs</em> you money.
            </p>

            <h2>Tools to run your own numbers</h2>
            <ul>
                <li><Link to="/calculator/mortgage">Standard mortgage calculator</Link> — for conventional scenarios</li>
                <li><Link to="/calculator/fha-loan">FHA loan calculator</Link> — with UFMIP + annual MIP</li>
                <li><Link to="/calculator/va-loan">VA loan calculator</Link> — with funding fee</li>
                <li><Link to="/calculator/jumbo-loan">Jumbo loan calculator</Link> — over $766,550</li>
                <li><Link to="/calculator/refinance">Refinance calculator</Link> — for the &ldquo;FHA → conventional&rdquo; future move</li>
                <li><Link to="/calculator/rent-vs-buy">Rent vs Buy calculator</Link> — when you&apos;re still deciding to buy at all</li>
            </ul>

            <p className="text-sm text-muted-foreground italic mt-8">
                Loan rates and program rules change frequently. Always confirm
                today&apos;s requirements with at least two lenders, and check
                FHA &amp; VA county loan limits at hud.gov and va.gov before
                making an offer.
            </p>
        </>
    );
}
